import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Save, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const Profile = () => {
  const { user, loading, roles } = useAuth();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    display_name: "",
    full_name: "",
    phone: "",
    location: "",
  });

  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("display_name, full_name, phone, location")
      .eq("user_id", user.id)
      .single()
      .then(({ data }) => {
        if (data) {
          setForm({
            display_name: data.display_name || "",
            full_name: data.full_name || "",
            phone: data.phone || "",
            location: data.location || "",
          });
        }
      });
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/auth" replace />;

  const isSeller = roles.includes("seller");

  const handleSave = async () => {
    if (isSeller && !form.phone.trim()) {
      toast({ title: "Téléphone requis pour les vendeurs", variant: "destructive" });
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        location: form.location,
      })
      .eq("user_id", user.id);

    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Profil mis à jour !" });
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="container py-8 flex-1 max-w-lg">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold">Mon Profil</h1>
            <p className="text-sm text-muted-foreground">Gérez vos informations personnelles</p>
          </div>
        </div>

        <div className="bg-card rounded-lg border p-6 space-y-5">
          <div>
            <label className="text-sm font-medium mb-1.5 block">Nom d'utilisateur public</label>
            <Input
              value={form.display_name}
              disabled
              className="bg-muted cursor-not-allowed"
            />
            <p className="text-xs text-muted-foreground mt-1">Choisi à l'inscription, il ne peut plus être modifié.</p>
          </div>

          <div>
            <label className="text-sm font-medium mb-1.5 block">Nom et prénom (privé)</label>
            <Input
              value={form.full_name}
              onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
              placeholder="Jean Dupont"
            />
            <p className="text-xs text-muted-foreground mt-1">Visible uniquement par vos partenaires de transaction.</p>
          </div>

          <div>
            <label className="text-sm font-medium mb-1.5 block">
              Numéro de téléphone {isSeller && <span className="text-destructive">*</span>}
            </label>
            <Input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              placeholder="+32 470 12 34 56"
            />
            <p className="text-xs text-muted-foreground mt-1">Transmis à l'acheteur uniquement après paiement de l'acompte.</p>
          </div>

          <div>
            <label className="text-sm font-medium mb-1.5 block">Commune de résidence</label>
            <Input
              value={form.location}
              onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
              placeholder="Ex: Chimay, Couvin, Momignies..."
            />
            <p className="text-xs text-muted-foreground mt-1">Visible dans le chat pour aider le vendeur à anticiper la livraison.</p>
          </div>

          <Button onClick={handleSave} disabled={saving} className="w-full gap-2">
            <Save className="h-4 w-4" /> {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Profile;
